import { useCallback, useEffect, useState } from "react";
import { Check, Crown, Loader2, Building2, CreditCard, FileText, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { BillingPanel } from "../BillingPanel";
import { WorkspacePanel } from "../WorkspacePanel";
import { CheckoutDialog } from "../billing/CheckoutDialog";
import { SubsectionTabs } from "./SubsectionTabs";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { fmtAmountMinor } from "../../lib/usePlans";

const PLAN_ORDER = ["free", "pro", "team", "business", "enterprise"];

const BILLING_TABS = [
  { key: "subscription", label: "Abonnement", path: "/compte/facturation/abonnement", icon: Crown },
  { key: "company", label: "Informations", path: "/compte/facturation/informations", icon: Building2 },
  { key: "payments", label: "Moyens de paiement", path: "/compte/facturation/paiement", icon: CreditCard },
  { key: "documents", label: "Factures", path: "/compte/facturation/factures", icon: FileText },
];

const BILLING_ACCESS_TAB = {
  key: "access",
  label: "Accès facturation",
  path: "/compte/facturation/acces",
  icon: UserCog,
};

const PAGE_COPY = {
  subscription: "Forfait, volume inclus, changement d'offre et résiliation.",
  company: "Coordonnées de l'entreprise et informations utilisées pour la facturation.",
  payments: "Cartes, prélèvements et autres moyens de paiement autorisés pour ce Workspace.",
  documents: "Factures, avoirs, statuts de paiement et documents PDF.",
  access: "Responsables autorisés à accéder à la facturation sans consommer de siège.",
};

export const BillingSectionPage = ({ section = "subscription" }) => {
  const { activeWorkspaceId, activeWorkspace, setUser } = useAuth();
  const role = activeWorkspace?.role || "member";
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutPlan, setCheckoutPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/subscription");
      setSub(data);
    } catch (e) {
      setSub(null);
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, activeWorkspaceId]);

  const canBilling = role === "owner" || role === "admin" || role === "billing_admin";
  const canManageBillingAccess = role === "owner" || role === "admin";
  const billingTabs = canManageBillingAccess ? [...BILLING_TABS, BILLING_ACCESS_TAB] : BILLING_TABS;

  if (!canBilling) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center"><h1 className="font-heading text-2xl font-bold">Accès non disponible</h1><p className="text-slate-500 mt-2">Votre rôle ne donne pas accès à la facturation.</p></div>;
  }
  if (loading || !sub) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const pc = sub.plan_config;
  const com = sub.commercial || {};
  const comCurrency = com.currency || "EUR";
  const pubActive = !!com.publication_id;
  const ent = sub.entitlements || {};

  const comPrice = (key) => {
    const p = com.plans?.[key];
    if (p) {
      if (p.requires_quote || p.monthly_price_minor == null) return { label: "Sur devis", ok: true };
      return { label: `${fmtAmountMinor(p.monthly_price_minor, p.currency || comCurrency)}/mois`, ok: true };
    }
    if (pubActive) return { label: "Tarif indisponible", ok: false };
    const lp = sub.all_plans?.[key];
    return { label: lp && lp.price_eur != null ? `${lp.price_eur} €/mois` : "Sur devis", ok: true };
  };

  const currentPrice = (() => {
    const p = com.current;
    if (p) {
      if (p.requires_quote || p.monthly_price_minor == null) return "Sur devis";
      return `${fmtAmountMinor(p.monthly_price_minor, p.currency || comCurrency)}/mois`;
    }
    if (pubActive) return "Tarif indisponible";
    return pc.price_eur != null ? `${pc.price_eur} €/mois` : "Sur devis";
  })();

  const changePlan = async (plan) => {
    if (role !== "owner") return;
    if (plan !== "free") { setCheckoutPlan(plan); return; }
    try {
      const { data } = await api.post("/account/plan", { plan });
      setUser(data.user);
      await load();
      toast.success(`Forfait ${data.user.plan_config?.name || "Free"} activé.`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="billing-section-page" className="space-y-6">
      <CheckoutDialog workspaceId={activeWorkspaceId} plan={checkoutPlan} onClose={() => setCheckoutPlan(null)} />
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-1"><Crown className="w-5 h-5" /><span className="text-xs font-semibold uppercase tracking-[0.16em]">Workspace</span></div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Facturation</h1>
        <p className="text-slate-500 mt-2">{PAGE_COPY[section] || PAGE_COPY.subscription}</p>
      </div>

      <SubsectionTabs items={billingTabs} active={section} />

      {section === "subscription" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><Crown className="w-5 h-5 text-amber-600" /><h2 className="font-heading text-lg font-semibold">Votre forfait</h2></div>
            <div className="flex items-baseline gap-2"><span className="font-heading text-3xl font-extrabold">{pc.name}</span><span className="text-slate-500">{currentPrice}</span></div>
            <p className="text-sm text-slate-500 mt-3">{ent.monthly_secret_soft_limit != null ? `${ent.monthly_secret_soft_limit} secrets inclus par mois` : "Volumes sur mesure"}</p>
          </div>

          <div className="glass rounded-2xl p-6">
            <h2 className="font-heading text-lg font-semibold mb-4">{role === "owner" ? "Changer de forfait" : "Forfait de l'espace"}</h2>
            {role !== "owner" ? (
              <p className="text-sm text-slate-500">Seul le propriétaire du Workspace peut changer de forfait. Forfait actuel : <span className="text-emerald-700 font-semibold">{pc.name}</span>.</p>
            ) : (
              <div className="space-y-2">
                {PLAN_ORDER.map((key) => {
                  const p = sub.all_plans?.[key];
                  if (!p) return null;
                  const active = sub.plan === key;
                  const price = comPrice(key);
                  return (
                    <div key={key} className={`flex items-center justify-between gap-3 rounded-lg px-4 py-3 border ${active ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                      <div><div className="text-sm font-semibold">{p.name}</div><div className={`text-xs ${price.ok ? "text-slate-500" : "text-amber-600"}`}>{price.label}</div></div>
                      {active ? <Badge variant="outline" className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30"><Check className="w-3 h-3 mr-1" /> Actuel</Badge> : <Button size="sm" variant="outline" disabled={!price.ok} onClick={() => changePlan(key)}>{price.ok ? "Choisir" : "Indisponible"}</Button>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {section === "access" ? (
        canManageBillingAccess ? (
          <div className="workspace-view-billing">
            <WorkspacePanel workspaceId={activeWorkspaceId} role={role} />
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <h2 className="font-heading text-xl font-bold">Accès non disponible</h2>
            <p className="text-slate-500 mt-2">Seuls le propriétaire et les administrateurs peuvent gérer les responsables facturation.</p>
          </div>
        )
      ) : (
        <div className={`billing-view-${section}`}>
          <BillingPanel workspaceId={activeWorkspaceId} role={role} plan={sub.plan} />
        </div>
      )}
    </div>
  );
};
